import React, { useEffect, useState } from "react";
import { GetCurrentStoreData, WebsitesState } from "../../store/websites/types";
import { connect } from "react-redux";
import {
  getAgentInstallData,
  getCurrentStoreData,
} from "../../store/websites/thunks";
import { getPathArray } from "../../helpers/getPathArray";
import { RouteComponentProps, withRouter } from "react-router-dom";
import _ from "lodash";
import { deleteAllPageDynamicData } from "../../store/websitePages/actions";
import "./AgentInstallPage.scss";
import { Alert, Button, Divider, Icon, message, Tooltip } from "antd";
import { updateWebsiteAsync } from "../../helpers/async/updateWebsiteAsync";
import { AuthState } from "../../store/auth/types";
import { ExternalLink, User, UserRound } from "lucide-react";
import { incrementAgentInstallsAsync } from "../../helpers/async/incrementAgentInstallsAsync";
import { saveMetaInState } from "../../store/meta/actions";
import { MetaState } from "../../store/meta/types";
import { distanceInWords } from "date-fns";
import capitalizeString from "../../helpers/strings/capitalizeString";
import { checkIfAgentsAvailable } from "../../store/user/thunks";

interface Props extends RouteComponentProps {
  history: any;
  websites: WebsitesState;
  auth: AuthState;
  getCurrentStoreData: GetCurrentStoreData;
  deleteAllPageDynamicData: any;
  getAgentInstallData: any;
  meta: MetaState;
  saveMetaInState: any;
  checkIfAgentsAvailable: any;

  agentData: any;
  saveWebsiteDataInStore: any;
}

interface InfoSectionProps {
  title?: string;
  info?: string;
  html?: string;
}
const InfoSection = (props: InfoSectionProps) => {
  return (
    <div className="info-section">
      {!!props.title && (
        <div className="info-section__title">{props.title}</div>
      )}
      {props.info !== undefined && (
        <div className="info-section__info">{props.info}</div>
      )}
      {!!props.html && (
        <div
          className="info-section__html"
          dangerouslySetInnerHTML={{ __html: props.html }}
        />
      )}
    </div>
  );
};

const AgentInstallPage = (props: Props) => {
  const { agentData } = props;
  const { currentWebsite } = props.getCurrentStoreData();
  const isInstalled = !!props.getAgentInstallData(agentData.id);

  const [isInstallLoading, setIsInstallLoading] = useState(false);
  const [isUninstallLoading, setIsUninstallLoading] = useState(false);

  useEffect(() => {
    let script = document.querySelector(
      `script[src="https://static.senja.io/dist/platform.js"]`
    ) as HTMLScriptElement;
    if (script) return;
    script = document.createElement("script");
    script.src = "https://static.senja.io/dist/platform.js";
    script.async = true;
    script.type = "text/javascript";
    document.body.append(script);
  }, []);

  if (!currentWebsite) return null;

  const installAgent = () => {
    setIsInstallLoading(true);
    const { currentWebsite } = props.getCurrentStoreData();
    const currentAgents = _.cloneDeep(
      _.get(currentWebsite, "agents_data.items", [])
    );
    currentAgents.push({
      id: agentData.id,
      status: "installed",
      installed_at: Date.now(),
    });

    updateWebsiteAsync({
      accessToken: props.auth.accessToken,
      subdomain: currentWebsite.subdomain,
      data: {
        agents_data: {
          ...currentWebsite.agents_data,
          items: currentAgents,
        },
      },
    })
      .then((response) => {
        const dataToStore = {
          data: {
            ...currentWebsite,
            agents_data: {
              ...currentWebsite.agents_data,
              items: currentAgents,
            },
          },
        };
        props.saveWebsiteDataInStore(
          dataToStore,
          _.get(currentWebsite, "subdomain")
        );
        props.history.push(
          `/${currentWebsite.subdomain}/agents/${agentData.id}`
        );
        incrementAgentInstallsAsync({
          accessToken: props.auth.accessToken,
          agentId: agentData.id,
        })
          .then((response) => {
            const newMeta = {
              ...props.meta.items[0],
              agents: response.data.new_agents,
            };
            props.saveMetaInState([newMeta]);
          })
          .catch((error) => {
            console.error(error);
            console.error(error.response);
          });
        message.success(`${agentData.title} installed`, 2);
      })
      .catch((error) => {
        console.error(error);
        console.error(error.response);
      })
      .finally(() => {
        setIsInstallLoading(false);
      });
  };

  const unInstallAgent = () => {
    setIsUninstallLoading(true);
    const { currentWebsite } = props.getCurrentStoreData();
    let currentAgents = _.cloneDeep(
      _.get(currentWebsite, "agents_data.items", [])
    );
    currentAgents = currentAgents.filter((agent) => agent.id !== agentData.id);

    updateWebsiteAsync({
      accessToken: props.auth.accessToken,
      subdomain: currentWebsite.subdomain,
      data: {
        agents_data: {
          ...currentWebsite.agents_data,
          items: currentAgents,
        },
      },
    })
      .then((response) => {
        const dataToStore = {
          data: {
            ...currentWebsite,
            agents_data: {
              ...currentWebsite.agents_data,
              items: currentAgents,
            },
          },
        };
        props.saveWebsiteDataInStore(
          dataToStore,
          _.get(currentWebsite, "subdomain")
        );
        message.success(`${agentData.title} uninstalled`, 2);
      })
      .catch((error) => {
        console.error(error);
        console.error(error.response);
      })
      .finally(() => {
        setIsUninstallLoading(false);
      });
  };

  const { isAvailable, message } = props.checkIfAgentsAvailable();

  return (
    <div className="agent-install-page">
      <div className="agent-install-page__container">
        <div className="agent-install-page__header">
          <div
            className="agent-install-page__logo-container"
            style={{
              background: agentData.logoBgColor,
            }}
          >
            <img className="agent-install-page__logo" src={agentData.logo} />
          </div>
          <div>
            <div className="agent-install-page__header-text">
              <h1 className="agent-install-page__title">{agentData.title}</h1>
              <div className="agent-install-page__subtitle">
                {agentData.subtitle}
              </div>
            </div>
            <div className="agent-install-page__button-box">
              {!isInstalled && agentData.isAvailable && (
                <>
                  <Tooltip
                    title={!!message ? message : ""}
                    overlayStyle={{
                      width: 170,
                    }}
                  >
                    <div>
                      <Button
                        onClick={installAgent}
                        type="primary"
                        loading={isInstallLoading}
                        className="agent-install-page__button"
                        disabled={!isAvailable}
                      >
                        Install
                      </Button>
                    </div>
                  </Tooltip>
                  {!isAvailable && agentData.projectUrl && (
                    <Button
                      onClick={() => {
                        window.open(agentData.projectUrl, "_blank");
                      }}
                      type="primary"
                      className="agent-install-page__button is-secondary"
                    >
                      <ExternalLink size={15} />
                      Manual install
                    </Button>
                  )}
                </>
              )}
              {!isInstalled && !agentData.isAvailable && (
                <>
                  <Button
                    type="primary"
                    className="agent-install-page__button"
                    disabled
                  >
                    Coming soon
                  </Button>
                </>
              )}
              {isInstalled && (
                <>
                  <Button
                    type="primary"
                    onClick={() => {
                      props.history.push(
                        `/${currentWebsite.subdomain}/agents/${agentData.id}`
                      );
                    }}
                    className="agent-install-page__button"
                  >
                    Open
                  </Button>
                  <Button
                    type="danger"
                    ghost
                    onClick={unInstallAgent}
                    loading={isUninstallLoading}
                    className="agent-install-page__button"
                  >
                    Uninstall
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="agent-install-page__info-section-container">
          <InfoSection title="Category" info={agentData.categories[0]} />
          <div className="agent-install-page__info-section-divider" />
          <InfoSection title="Users" info={agentData.installsCount} />
          <div className="agent-install-page__info-section-divider" />
          <InfoSection title="Author" info={agentData.author} />
          <div className="agent-install-page__info-section-divider" />
          <InfoSection
            title="Last update"
            info={capitalizeString(
              distanceInWords(new Date(), new Date(agentData.lastUpdate), {
                addSuffix: true,
              })
            )}
          />
          {agentData.badges && agentData.badges.length > 0 && (
            <div className="agent-install-page__info-section-divider" />
          )}
          {agentData.badges &&
            agentData.badges.length > 0 &&
            agentData.badges.map((badge, index) => {
              return (
                <>
                  <InfoSection title={badge.title} html={badge.html} />
                  {index < agentData.badges.length - 1 && (
                    <div className="agent-install-page__info-section-divider" />
                  )}
                </>
              );
            })}
        </div>
        <div className="agent-install-page__description">
          <div dangerouslySetInnerHTML={{ __html: agentData.description }} />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    websites: state.websites,
    auth: state.auth,
    meta: state.meta,
  };
};
export default connect(mapStateToProps, {
  getCurrentStoreData,
  deleteAllPageDynamicData,
  getAgentInstallData,
  saveMetaInState,
  checkIfAgentsAvailable,
})(withRouter(AgentInstallPage));
